import { StripeCardElementChangeEvent, StripeError } from '@stripe/stripe-js';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import classnames from 'classnames';
import { useContext, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as api from '../../../api';
import * as store from '../../../reducers/store';
import {
  BillingDetails,
  extractBillingDetailsFromFormAnswers,
  getFormattedCurrencyAmount,
  isUnsupportedCard,
  mergeBillingDetails,
} from './utils';

import { CheckBox } from '../../../components/CheckBox';
import { InfoMessage } from '../../../components/InfoMessage';
import { PaymentContext } from '../PaymentInfoProvider';
import BillingDetailsForm from '../BillingDetailsForm';
import BrowserPayElement from './BrowserPayElement';
import PaymentLoadingModal from '../Modal/Loading';
import PaymentSuccessModal from '../Modal/Success';
import SpinnerButton from '../../../components/SpinnerButton';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../utils/formSet';

import styles from '../index.module.css';

export interface PaymentsPageProps {
  practiceName?: string;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  onSuccess: (saveValue: string) => Promise<void>;
}

interface StateProps {
  forms: store.Forms;
  formSets: store.FormSets;
  selectedFormSetId: string;
  unsupportedCardBrands: store.Loaded['masterPracticePreference']['unsupportedCardBrands'];
}

const PaymentsPage: React.FC<PaymentsPageProps & StateProps> = ({
  field,
  forms,
  onSuccess,
  formSets,
  selectedFormSetId,
  unsupportedCardBrands,
}) => {
  const { t } = useTranslation();
  const params = useParams<{ authenticationCode: string }>();
  const stripe = useStripe();
  const elements = useElements();
  const paymentInfo = useContext(PaymentContext).data;
  const encounter = formSets[selectedFormSetId]?.encounter;

  const [billingDetails, setBillingDetails] = useState<BillingDetails>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [saveCardInfo, setSaveCardInfo] = useState(true);

  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<
    StripeCardElementChangeEvent['error'] | StripeError
  >();

  const [showSuccess, setShowSuccess] = useState(false);
  const [iconColor, setIconColor] = useState('');
  const [incompleteIconColor, setIncompleteIconColor] = useState('');

  useEffect(() => {
    // hack to get CSS variables for button into card element, but only on mount
    // to prevent variant violation
    setIconColor(
      getComputedStyle(document.body).getPropertyValue('--color-primary'),
    );
  }, [setIconColor]);

  useEffect(() => {
    // requests are split into two to avoid violation constraint warning
    if (!iconColor) return;
    setIncompleteIconColor(
      getComputedStyle(document.body).getPropertyValue('--color-deep-gray'),
    );
  }, [iconColor, setIncompleteIconColor]);

  useEffect(() => {
    const formElement = document.getElementById('form-container');
    if (!formElement) return;

    formElement.classList.add(styles.Gray);
    return () => formElement.classList.remove(styles.Gray);
  }, []);

  useEffect(() => {
    if (!field) return;
    try {
      const fieldDetails: BillingDetails = field.value
        ? { ...JSON.parse(field.value), skipped: undefined }
        : {};
      const enteredDetails = extractBillingDetailsFromFormAnswers(forms);
      const overrideDetails = {
        address: { country: 'US' },
      };
      setBillingDetails(
        mergeBillingDetails(fieldDetails, enteredDetails, overrideDetails),
      );
    } catch {
      // got a non-object JSON string as a value, which shouldn't happen
    }
  }, [field, setBillingDetails, forms]);

  const handleSuccess = async () => {
    const saveValue = JSON.stringify(billingDetails);
    await onSuccess(saveValue);
  };

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    if (event.complete !== cardComplete) {
      const isUnsupportedCardBrand = isUnsupportedCard(
        unsupportedCardBrands,
        event.brand,
      );

      isUnsupportedCardBrand
        ? setCardError({
            type: 'validation_error',
            code: 'unsupported_card',
            message: t(
              'This card is not supported. Please try a different card.',
            ),
          })
        : setCardComplete(event.complete);
    }
    if (cardError?.code !== event.error?.code) setCardError(event.error);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (!stripe || !elements || !paymentInfo || !encounter) return;
    event.preventDefault();
    const targetElement = elements.getElement(CardElement);
    if (!targetElement) return;
    setIsSubmitting(true);

    const { error } = await stripe.confirmCardPayment(
      paymentInfo.clientSecret,
      {
        payment_method: {
          card: targetElement,
          billing_details: billingDetails,
          metadata: {
            encounterId: encounter.id,
          },
        },
        setup_future_usage: saveCardInfo ? 'off_session' : undefined,
      },
    );
    setIsSubmitting(false);

    if (error) return setCardError(error);

    await api.client.Payment.refreshPaymentStatus(
      params.authenticationCode,
      selectedFormSetId,
    );
    setShowSuccess(true);
  };

  if (!paymentInfo) return null;

  return (
    <>
      <PaymentSuccessModal
        isOpen={showSuccess && !isSubmitting}
        emailFromBilling={billingDetails.email}
        onRequestClose={handleSuccess}
        selectedFormSetId={selectedFormSetId}
      />
      <PaymentLoadingModal isOpen={isSubmitting} />

      <form onSubmit={handleSubmit}>
        {paymentInfo && (
          <BrowserPayElement
            paymentInfo={paymentInfo}
            onPaymentError={setCardError}
            onPaymentSuccess={handleSuccess}
          />
        )}

        <BillingDetailsForm
          billingDetails={billingDetails}
          setBillingDetails={setBillingDetails}
        />

        <div
          className={classnames(styles.PaymentInformation, styles.HeaderLabel)}
        >
          {t('Add your payment information')}
        </div>
        <div
          className={classnames(styles.Form, styles.CardElementContainer, {
            [styles.Error]: cardError,
          })}
        >
          <CardElement
            onChange={handleCardChange}
            options={{
              hidePostalCode: true,
              iconStyle: 'solid',
              style: {
                base: { iconColor: incompleteIconColor },
                complete: { iconColor },
              },
            }}
          />
        </div>

        <div className={styles.CheckboxContainer}>
          <CheckBox
            checked={saveCardInfo}
            label={t('Save payment information')}
            saveFieldValue={(value) => setSaveCardInfo(value === 'true')}
            updateFieldValue={() => {}}
          />
        </div>
        {cardError && cardError.message && (
          <InfoMessage className={styles.CardError} isError={true}>
            {cardError.message}
          </InfoMessage>
        )}
        {/* TODO: Add copy informing patient that we will charge on their behalf in the future */}

        <SpinnerButton
          className={styles.PayButton}
          disabled={!stripe || !cardComplete || isSubmitting}
          type="submit"
          showSpinner={isSubmitting}
        >
          {`${t('Pay')} ${getFormattedCurrencyAmount(
            paymentInfo.amount || 0,
            paymentInfo.currency || 'usd',
          )}`}
        </SpinnerButton>
      </form>
    </>
  );
};

const mapStateToProps = (store: store.FormSetSelected): StateProps => ({
  forms: getSelectedForms(store.formSets, store.selectedFormSetId),
  formSets: store.formSets,
  selectedFormSetId: store.selectedFormSetId,
  unsupportedCardBrands:
    getSelectedPracticePreference(store).unsupportedCardBrands,
});

export default connect(mapStateToProps)(PaymentsPage);
